import React, { useState, useEffect, useMemo } from 'react';
import {
    Popover,
    Box,
    Typography,
    Checkbox,
    Button,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    useTheme,
    TextField,
    Chip,
    InputAdornment,
    Autocomplete,
    Switch,
} from '@mui/material';
import { FiPlus } from 'react-icons/fi';
import { IoFilterSharp } from 'react-icons/io5';

import CloseIcon from '../../icons/CloseIcon';
import { PinIcon } from '../../icons/PinIcon';
import { SearchIcon } from '../../icons/SearchIcon';

import CoreInput from './CoreInput';
import { ColumnFilterType, FilterOperator, SortOption } from '../../../types/table';
import Column from './Column';
import { MatchContentButtons } from './table/MatchContentButtons';
import { SortContentButtons } from './table/SortContentButtons';
import { toStartCase } from '../../../utils';

export interface ColumnFilterPopoverProps {
    columnId: string;
    columnLabel: string;
    options: string[];
    /** Human-readable label for a raw option value (falls back to the value itself if omitted) */
    getOptionLabel?: (value: string) => string;
    selectedValues: string[];
    /** Current operator for list-type filters; used when filterType is 'list' */
    selectedOperator?: FilterOperator;
    onApply: (columnId: string, values: string[], operator?: FilterOperator) => void;
    onClear: (columnId: string) => void;
    isActive?: boolean;
    filterType?: ColumnFilterType;
    /** Max selectable date (YYYY-MM-DD) for date/dateRange filters */
    maxDate?: string;
    /** Input placeholder for text filters (overrides the default hint) */
    placeholder?: string;
    /** Current sort direction for this column; providing this (with onSortChange) shows the Sort section */
    sortDirection?: 'asc' | 'desc' | false;
    /** Set the sort direction for this column directly; providing this shows the Sort section */
    onSortChange?: (direction: 'asc' | 'desc' | null) => void;
    /** Whether this column is currently pinned; providing onTogglePin shows the Pin row */
    isPinned?: boolean;
    /** Toggle the pinned state of this column; providing this shows the Pin row */
    onTogglePin?: () => void;
    /** Whether the column can be pinned/unpinned (default true) */
    canPin?: boolean;
    /** Called when the popover opens/closes, so the parent can keep the header cell highlighted while it's open */
    onOpenChange?: (open: boolean) => void;
}

const isTruthyBooleanFilterValue = (value: string) => {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === 'yes' || normalized === '1';
};

const isFalsyBooleanFilterValue = (value: string) => {
    const normalized = value.trim().toLowerCase();
    return normalized === 'false' || normalized === 'no' || normalized === '0';
};

const parseBooleanFilterValues = (values: string[]) => ({
    trueSelected: values.some(isTruthyBooleanFilterValue),
    falseSelected: values.some(isFalsyBooleanFilterValue),
});

const ColumnFilterPopover: React.FC<ColumnFilterPopoverProps> = ({
    columnId,
    options,
    getOptionLabel,
    selectedValues,
    selectedOperator = 'and',
    onApply,
    onClear,
    isActive = false,
    filterType = 'list',
    maxDate,
    placeholder,
    sortDirection,
    onSortChange,
    isPinned = false,
    onTogglePin,
    canPin = true,
    onOpenChange,
}) => {
    const theme = useTheme();

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [searchText, setSearchText] = useState('');
    const [localSelected, setLocalSelected] = useState<Set<string>>(new Set(selectedValues));
    const [localOperator, setLocalOperator] = useState<FilterOperator>(selectedOperator);
    const [localSort, setLocalSort] = useState<SortOption>(sortDirection || 'default');

    const [dateValue, setDateValue] = useState('');
    const [dateRangeStart, setDateRangeStart] = useState('');
    const [dateRangeEnd, setDateRangeEnd] = useState('');
    const [priceRangeMin, setPriceRangeMin] = useState('');
    const [priceRangeMax, setPriceRangeMax] = useState('');
    const [textValue, setTextValue] = useState('');
    const [textTagValues, setTextTagValues] = useState<string[]>([]);
    const [booleanValue, setBooleanValue] = useState<'true' | 'false' | null>(null);

    const maxDateForStart = useMemo(() => {
        const candidates = [dateRangeEnd, maxDate].filter(Boolean) as string[];
        if (candidates.length === 0) {
            return undefined;
        }

        return candidates.reduce((earliest, candidate) =>
            candidate < earliest ? candidate : earliest,
        );
    }, [dateRangeEnd, maxDate]);

    const open = Boolean(anchorEl);
    const FiPlusIcon = FiPlus as any;
    const FilterTriggerIcon = IoFilterSharp as any;

    // Sync local state with props when popover opens
    useEffect(() => {
        if (open) {
            setLocalSelected(new Set(selectedValues));
            setLocalOperator(selectedOperator);
            setLocalSort(sortDirection || 'default');
            setSearchText('');

            // Parse value from selectedValues based on filterType
            switch (filterType) {
                case 'date':
                    setDateValue(selectedValues.length > 0 ? (selectedValues[0] || '') : '');
                    break;
                case 'dateRange':
                    setDateRangeStart(selectedValues[0] || '');
                    setDateRangeEnd(selectedValues[1] || '');
                    break;
                case 'priceRange':
                    setPriceRangeMin(selectedValues[0] || '');
                    setPriceRangeMax(selectedValues[1] || '');
                    break;
                case 'text':
                    setTextTagValues(selectedValues.filter(Boolean));
                    setTextValue('');
                    break;
                case 'boolean': {
                    const { trueSelected, falseSelected } = parseBooleanFilterValues(selectedValues);
                    if (trueSelected) setBooleanValue('true');
                    else if (falseSelected) setBooleanValue('false');
                    else setBooleanValue(null);
                    break;
                }
                default:
                    break;
            }
        }
    }, [open, selectedValues, selectedOperator, filterType, sortDirection]);

    const handleSortToggle = (_: React.MouseEvent<HTMLElement>, value: SortOption | null) => {
        if (value == null) return;

        setLocalSort(value);
    };

    // Filter options based on search text -- match against the display label when one is
    // provided (e.g. a state code's full name), not just the raw underlying value.
    const filteredOptions = useMemo(() => {
        if (!searchText.trim()) return options;
        const lowerSearch = searchText.toLowerCase();
        return options.filter(opt =>
            opt?.toLowerCase().includes(lowerSearch) ||
            getOptionLabel?.(opt)?.toLowerCase().includes(lowerSearch)
        );
    }, [options, searchText, getOptionLabel]);

    // Check if all visible options are selected
    const allVisibleSelected = useMemo(() => {
        return filteredOptions.length > 0 && 
            filteredOptions.every(opt => localSelected.has(opt));
    }, [filteredOptions, localSelected]);

    // Check if some (but not all) visible options are selected
    const someVisibleSelected = useMemo(() => {
        const selectedCount = filteredOptions.filter(opt => localSelected.has(opt)).length;
        return selectedCount > 0 && selectedCount < filteredOptions.length;
    }, [filteredOptions, localSelected]);

    /** Text filter: Excluding already selected values from options list */
    const textAutocompleteOptions = useMemo(() => {
        const added = new Set(textTagValues);
        return options.filter(opt => opt != null && opt !== '' && !added.has(opt));
    }, [options, textTagValues]);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        onOpenChange?.(true);
    };

    const handleClose = () => {
        setAnchorEl(null);
        onOpenChange?.(false);
    };

    const handleToggleOption = (option: string) => {
        const newSelected = new Set(localSelected);
        if (newSelected.has(option)) {
            newSelected.delete(option);
        } else {
            newSelected.add(option);
        }
        setLocalSelected(newSelected);
    };

    const handleSelectAll = () => {
        if (allVisibleSelected) {
            // Deselect all visible options
            const newSelected = new Set(localSelected);
            filteredOptions.forEach(opt => newSelected.delete(opt));
            setLocalSelected(newSelected);
        } else {
            // Select all visible options
            const newSelected = new Set(localSelected);
            filteredOptions.forEach(opt => newSelected.add(opt));
            setLocalSelected(newSelected);
        }
    };

    const handleApply = () => {
        onSortChange?.(localSort === 'default' ? null : localSort);

        switch (filterType) {
            case 'date':
                dateValue ? onApply(columnId, [dateValue]) : onClear(columnId);
                break;
            case 'dateRange':
                (dateRangeStart || dateRangeEnd) ? onApply(columnId, [dateRangeStart, dateRangeEnd]) : onClear(columnId);
                break;
            case 'priceRange':
                (priceRangeMin || priceRangeMax) ? onApply(columnId, [priceRangeMin, priceRangeMax]) : onClear(columnId);
                break;
            case 'text':
                textTagValues.length > 0 ? onApply(columnId, textTagValues, localOperator) : onClear(columnId);
                break;
            case 'boolean':
                booleanValue ? onApply(columnId, [booleanValue]) : onClear(columnId);
                break;
            default:
                onApply(columnId, Array.from(localSelected), 'and');
                break;
        }
        handleClose();
    };

    const handleClear = () => {
        switch (filterType) {
            case 'date':
                setDateValue('');
                break;
            case 'dateRange':
                setDateRangeStart('');
                setDateRangeEnd('');
                break;
            case 'priceRange':
                setPriceRangeMin('');
                setPriceRangeMax('');
                break;
            case 'text':
                setTextValue('');
                setTextTagValues([]);
                break;
            case 'boolean':
                setBooleanValue(null);
                break;
            default:
                setLocalSelected(new Set());
                break;
        }
        onClear(columnId);
        handleClose();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleApply();
        }
    };

    const handleAddTextValue = (explicitValue?: string) => {
        const raw = explicitValue !== undefined ? explicitValue : textValue;
        const trimmed = raw.trim();

        if (trimmed) {
            setTextTagValues(prev => (prev.includes(trimmed) ? prev : [...prev, trimmed]));
            setTextValue('');
        }
    };

    const handleTextInputKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddTextValue();
        }
    };

    const handleRemoveTextTag = (index: number) => {
        setTextTagValues(prev => prev.filter((_, i) => i !== index));
    };

    return (
        <>
            <IconButton
                size="small"
                onClick={handleClick}
                sx={{
                    ml: 0.5,
                    p: 0.5,
                    borderRadius: '6px',
                    color: isActive ? "white" : theme.palette.text.secondary,
                    backgroundColor: isActive ? 'grey.500' : 'transparent',
                    opacity: isActive ? 1 : 0.6,
                    '&:hover': {
                        opacity: isActive? 0.6 : 1,
                        color: isActive ? "white" : theme.palette.text.secondary,
                        backgroundColor: isActive ? 'grey.500' : 'transparent',
                    },
                }}
            >
                <FilterTriggerIcon size={13} />
            </IconButton>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                onClick={(e) => e.stopPropagation()}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            width: 280,
                            maxHeight: 640,
                            borderRadius: 1.5,
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                        },
                    },
                }}
            >
                {/* Pin */}
                {onTogglePin && (
                    <>
                        <ListItemButton
                            dense
                            disabled={!canPin}
                            onClick={canPin ? () => { onTogglePin(); handleClose(); } : undefined}
                            sx={{ px: 2, py: 1.5, gap: 1 }}
                        >
                            <Box sx={{ display: 'flex', color: isPinned ? 'custom.brandTeal' : 'text.secondary' }}>
                                <PinIcon width={16} height={16} />
                            </Box>
                            <Typography
                                variant="body2"
                                fontWeight={500}
                                color={isPinned ? 'custom.brandTeal' : 'text.primary'}
                            >
                                {isPinned ? 'Unpin This Column' : 'Pin This Column'}
                            </Typography>
                        </ListItemButton>
                        <Divider />
                    </>
                )}

                {/* Sort */}
                {onSortChange && (
                    <SortContentButtons localSort={localSort} handleSortToggle={handleSortToggle} />
                )}

                {(() => {
                    switch (filterType) {
                        case 'date':
                            return (
                                <Box sx={{ px: 2, py: 2 }} onClick={(e) => e.stopPropagation()}>
                                    <TextField
                                        type="date"
                                        value={dateValue}
                                        onChange={(e) => setDateValue(e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                        onKeyDown={handleKeyDown}
                                        size="small"
                                        fullWidth
                                        slotProps={{ htmlInput: { max: maxDate } }}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Box>
                            );
                        case 'dateRange':
                            return (
                                <Box sx={{ px: 2, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }} onClick={(e) => e.stopPropagation()}>
                                    <Column>
                                        <Typography variant="body2">From (or exact match)</Typography>
                                        <TextField
                                            type="date"
                                            value={dateRangeStart}
                                            onChange={(e) => setDateRangeStart(e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            onKeyDown={handleKeyDown}
                                            size="small"
                                            fullWidth
                                            slotProps={{
                                                htmlInput: { max: maxDateForStart },
                                            }}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Column>
                                    <Column>
                                        <Typography variant="body2">To (optional, range only)</Typography>
                                        <TextField
                                            type="date"
                                            value={dateRangeEnd}
                                            onChange={(e) => setDateRangeEnd(e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            onKeyDown={handleKeyDown}
                                            size="small"
                                            fullWidth
                                            slotProps={{
                                                htmlInput: {
                                                    min: dateRangeStart || undefined,
                                                    max: maxDate,
                                                },
                                            }}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Column>
                                </Box>
                            );
                        case 'priceRange':
                            return (
                                <Box sx={{ px: 2, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }} onClick={(e) => e.stopPropagation()}>
                                    <Column>
                                        <Typography variant="body2">Min (or exact match)</Typography>
                                        <TextField
                                            type="number"
                                            value={priceRangeMin}
                                            onChange={(e) => setPriceRangeMin(e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            onKeyDown={handleKeyDown}
                                            size="small"
                                            fullWidth
                                            inputProps={{ min: 0, step: 'any' }}
                                            slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                                        />
                                    </Column>
                                    <Column>
                                        <Typography variant="body2">Max (optional, range only)</Typography>
                                        <TextField
                                            type="number"
                                            value={priceRangeMax}
                                            onChange={(e) => setPriceRangeMax(e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            onKeyDown={handleKeyDown}
                                            size="small"
                                            fullWidth
                                            inputProps={{ min: 0, step: 'any' }}
                                            slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                                        />
                                    </Column>
                                </Box>
                            );
                        case 'boolean':
                            return (
                                <Box
                                    sx={{
                                        px: 2,
                                        py: 2,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 2,
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            fontWeight={booleanValue === 'false' ? 600 : 400}
                                            color={
                                                booleanValue === 'false'
                                                    ? 'text.primary'
                                                    : 'text.secondary'
                                            }
                                        >
                                            No
                                        </Typography>
                                        <Switch
                                            size="small"
                                            checked={booleanValue === 'true'}
                                            onChange={(e) =>
                                                setBooleanValue(
                                                    e.target.checked ? 'true' : 'false',
                                                )
                                            }
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                        <Typography
                                            variant="body2"
                                            fontWeight={booleanValue === 'true' ? 600 : 400}
                                            color={
                                                booleanValue === 'true'
                                                    ? 'text.primary'
                                                    : 'text.secondary'
                                            }
                                        >
                                            Yes
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        case 'text':
                            return (
                                <Box sx={{ px: 2, py: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }} onClick={(e) => e.stopPropagation()}>
                                    <MatchContentButtons localOperator={localOperator} setLocalOperator={setLocalOperator} />

                                    {options.length > 0 ? (
                                        <Autocomplete
                                            freeSolo
                                            /** Helps in clearing the input value when the user selects an option. */
                                            value={null}
                                            options={textAutocompleteOptions}
                                            /** Display only -- onChange below still receives (and adds) the raw option value. */
                                            getOptionLabel={(option) => getOptionLabel?.(String(option)) || String(option)}
                                            inputValue={textValue}
                                            onInputChange={(_, v) => setTextValue(v)}
                                            onChange={(_, value) => {
                                                if (value !== null && String(value).trim()) {
                                                    handleAddTextValue(String(value));
                                                }
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleAddTextValue();
                                                }
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    placeholder={placeholder ?? "Type to search or enter value..."}
                                                    size="small"
                                                    variant="standard"
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            )}
                                            filterOptions={(opts, { inputValue }) => {
                                                const trimmed = inputValue.trim().toLowerCase();
                                                const filtered = !trimmed
                                                    ? opts
                                                    : opts.filter(opt =>
                                                        (opt ?? '').toLowerCase().includes(trimmed)
                                                    );
                                                return filtered.slice(0, 50);
                                            }}
                                        />
                                    ) : (
                                        <CoreInput
                                            placeholder={placeholder ?? "Enter filter value..."}
                                            value={textValue}
                                            onChange={(e) => setTextValue(e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            onKeyDown={handleTextInputKeyDown}
                                            size="small"
                                            autoFocus
                                            slotProps={{
                                                htmlInput: { 'aria-label': 'Filter value' },
                                            }}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleAddTextValue()}
                                                            aria-label="Add value"
                                                            sx={{ p: 0.5 }}
                                                        >
                                                            <FiPlusIcon size={16} />
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    )}

                                    {textTagValues.length > 0 && (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, maxHeight: 200,
                                            overflow: 'auto', }}>
                                            {textTagValues.map((tag, index) => (
                                                <Chip
                                                    key={`${tag}-${index}`}
                                                    label={getOptionLabel?.(tag) || tag}
                                                    size="small"
                                                    onDelete={() => handleRemoveTextTag(index)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    sx={{ maxWidth: '100%' }}
                                                />
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            );
                        default:
                            return (
                                <>
                                    <Box sx={{ px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }} onClick={(e) => e.stopPropagation()}>
                                        <CoreInput
                                            placeholder="Search..."
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            size="small"
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <SearchIcon />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Box>
                                    <Box sx={{ px: 1 }}>
                                        <ListItemButton
                                            dense
                                            onClick={handleSelectAll}
                                            sx={{ borderRadius: 1, py: 0.1 }}
                                        >
                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                                <Checkbox
                                                    size="small"
                                                    checked={allVisibleSelected}
                                                    indeterminate={someVisibleSelected}
                                                    onChange={handleSelectAll}
                                                    onClick={(e) => e.stopPropagation()}
                                                    sx={{paddingLeft: 0}}
                                                />
                                            </ListItemIcon>
                                            <ListItemText 
                                                primary="Select All" 
                                                primaryTypographyProps={{ 
                                                    variant: 'body2',
                                                    fontWeight: 500,
                                                }}
                                            />
                                        </ListItemButton>
                                    </Box>
                                    <Divider />
                                    <List
                                        dense
                                        sx={{
                                            maxHeight: 200,
                                            overflow: 'auto',
                                            px: 1,
                                            py: 0.5,
                                        }}
                                    >
                                        {filteredOptions.length === 0 ? (
                                            <ListItem>
                                                <ListItemText
                                                    primary="No options found"
                                                    primaryTypographyProps={{
                                                        variant: 'body2',
                                                        color: 'text.secondary',
                                                        textAlign: 'center',
                                                    }}
                                                />
                                            </ListItem>
                                        ) : (
                                            filteredOptions.map((option) => (
                                                <ListItemButton
                                                    key={option}
                                                    dense
                                                    onClick={() => handleToggleOption(option)}
                                                    sx={{ borderRadius: 1, py: 0.1 }}
                                                >
                                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                                        <Checkbox
                                                            size="small"
                                                            checked={localSelected.has(option)}
                                                            onChange={() => handleToggleOption(option)}
                                                            onClick={(e) => e.stopPropagation()}
                                                            sx={{paddingLeft: 0}}
                                                        />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={option ? (getOptionLabel?.(option) || toStartCase(option)) : '(No option available)'}
                                                        primaryTypographyProps={{
                                                            variant: 'body2',
                                                            noWrap: true,
                                                            title: option,
                                                            fontWeight: localSelected.has(option) ? 600 : 400,
                                                            color: localSelected.has(option) ? 'text.primary' : 'text.secondary',
                                                        }}
                                                    />
                                                </ListItemButton>
                                            ))
                                        )}
                                    </List>
                                </>
                            );
                    }
                })()}

                <Divider />

                {/* Action Buttons */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        px: 2,
                        py: 1.5,
                        gap: 1,
                    }}
                >
                    <Button
                        size="small"
                        variant="text"
                        color="error"
                        onClick={handleClear}
                        startIcon={<CloseIcon size={14} />}
                        sx={{ textTransform: 'none' }}
                    >
                        Clear Filters
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        onClick={handleApply}
                        sx={{ textTransform: 'none', borderRadius: 2, px: 3 }}
                    >
                        Apply
                    </Button>
                </Box>
            </Popover>
        </>
    );
};

export default ColumnFilterPopover;
